import type { CoaAccountRow } from 'store/chart-of-accounts/models';

export type AccountDisplayRowFields = {
  accountId?: string | null;
  accountCode?: string | null;
  accountName?: string | null;
};

export type AccountNameOption = {
  label: string;
  value: string;
  /** Plain account name for API save (without code prefix). */
  name?: string;
};
export type AccountNameGroupOption = {
  label: string;
  options: AccountNameOption[];
};

export function coaRowId(row: CoaAccountRow): string | undefined {
  const raw =
    row.id ??
    (row as { accountId?: string }).accountId ??
    (row as { uuid?: string }).uuid;
  if (raw == null || String(raw).trim() === '') return undefined;
  return String(raw).trim();
}

/** Dropdown / cell label: `{code} - {name}` when code exists (Xero / QB / Zoho). */
export function formatCoaAccountOptionLabel(
  name: string,
  code: string | null | undefined,
  showCodeInLabel: boolean
): string {
  const n = (name ?? '').trim();
  const c = (code ?? '').trim();
  if (!n) return c || '-';
  if (showCodeInLabel && c) return `${c} - ${n}`;
  return n;
}

/** Flat account dropdown options sorted by account name (no type grouping). */
export function buildAccountNameOptionsFromCoa(
  rows: CoaAccountRow[],
  showCodeInLabel: boolean
): AccountNameOption[] {
  return rows
    .map((row) => {
      const name = (row.name ?? '').trim();
      const id = coaRowId(row);
      const value = id ?? name;
      if (!value) return null;
      return {
        label: formatCoaAccountOptionLabel(name, row.code, showCodeInLabel),
        value,
        name: name || undefined,
      };
    })
    .filter((o): o is AccountNameOption => o != null)
    .sort((a, b) =>
      (a.name ?? a.label).localeCompare(b.name ?? b.label, undefined, {
        sensitivity: 'base',
      })
    );
}

export function buildAccountNameByIdMapFromOptions(
  flat: AccountNameOption[],
  grouped: AccountNameGroupOption[]
): Map<string, string> {
  const map = new Map<string, string>();
  const add = (o: AccountNameOption) => {
    const id = String(o.value).trim();
    const name = (o.name ?? o.label ?? '').trim();
    if (!id || !name) return;
    map.set(id, name);
    map.set(id.toLowerCase(), name);
  };
  flat.forEach(add);
  grouped.forEach((g) => (g.options ?? []).forEach(add));
  return map;
}

export function buildAccountNameByIdMap(
  rows: CoaAccountRow[]
): Map<string, string> {
  const map = new Map<string, string>();
  for (const row of rows) {
    const id = coaRowId(row);
    if (!id) continue;
    const name = (row.name ?? '').trim();
    if (!name) continue;
    map.set(id, name);
    map.set(id.toLowerCase(), name);
  }
  return map;
}

function findAccountOptionByKey(
  key: string,
  options: AccountNameOption[]
): AccountNameOption | undefined {
  const trimmed = key.trim();
  if (!trimmed) return undefined;
  const lower = trimmed.toLowerCase();
  return (
    options.find((o) => String(o.value) === trimmed) ??
    options.find((o) => String(o.value).toLowerCase() === lower) ??
    options.find((o) => o.label === trimmed)
  );
}

/** Plain account name for save / validation (no code prefix). */
export function lookupAccountNameInCoa(
  key: string,
  map: Map<string, string>,
  options: AccountNameOption[]
): string | undefined {
  const trimmed = key.trim();
  if (!trimmed) return undefined;
  const lower = trimmed.toLowerCase();
  const opt = findAccountOptionByKey(trimmed, options);
  const fromOption = (opt?.name ?? '').trim();
  if (fromOption) return fromOption;
  const fromMap = map.get(trimmed) ?? map.get(lower);
  if (fromMap) return fromMap;
  const fromLabel = (opt?.label ?? '').trim();
  return fromLabel || undefined;
}

/** Display label for table cells and selects (`{code} - {name}` when configured). */
export function lookupAccountDisplayInCoa(
  key: string,
  map: Map<string, string>,
  options: AccountNameOption[]
): string | undefined {
  const trimmed = key.trim();
  if (!trimmed) return undefined;
  const lower = trimmed.toLowerCase();
  const opt = findAccountOptionByKey(trimmed, options);
  const fromOption = (opt?.label ?? '').trim();
  if (fromOption) return fromOption;
  return map.get(trimmed) ?? map.get(lower);
}

/** Resolve Account Name column text from transaction fields + COA. */
export function resolveAccountDisplayLabel(
  row: AccountDisplayRowFields | undefined,
  cellVal: unknown,
  accountNameByIdMap: Map<string, string>,
  accountNameOptions: AccountNameOption[],
  debugContext?: string
): string {
  const accountId =
    row?.accountId != null && String(row.accountId).trim() !== ''
      ? String(row.accountId).trim()
      : '';
  const accountCode =
    row?.accountCode != null && String(row.accountCode).trim() !== ''
      ? String(row.accountCode).trim()
      : '';
  const accountName =
    row?.accountName != null && String(row.accountName).trim() !== ''
      ? String(row.accountName).trim()
      : '';
  const fromCell =
    cellVal != null && String(cellVal).trim() !== ''
      ? String(cellVal).trim()
      : '';

  // Prefer cell value (inline edit / drawer draft) over stale row.accountId.
  const lookupKeys = [fromCell, accountId, accountCode].filter(
    (k, i, arr) => k && arr.indexOf(k) === i
  );

  for (const key of lookupKeys) {
    const resolved = lookupAccountDisplayInCoa(
      key,
      accountNameByIdMap,
      accountNameOptions
    );
    if (resolved) return resolved;
  }

  if (accountName) {
    const resolved = lookupAccountDisplayInCoa(
      accountName,
      accountNameByIdMap,
      accountNameOptions
    );
    return resolved ?? accountName;
  }

  if (fromCell) {
    const resolved = lookupAccountDisplayInCoa(
      fromCell,
      accountNameByIdMap,
      accountNameOptions
    );
    return resolved ?? fromCell;
  }
  if (accountCode) {
    const resolved = lookupAccountDisplayInCoa(
      accountCode,
      accountNameByIdMap,
      accountNameOptions
    );
    return resolved ?? accountCode;
  }

  if (process.env.NODE_ENV === 'development' && debugContext) {
    console.debug(
      `[TransactionsTab] account display unresolved (${debugContext})`,
      {
        accountId: accountId || null,
        accountCode: accountCode || null,
        accountName: accountName || null,
        cellVal: fromCell || null,
        coaMapSize: accountNameByIdMap.size,
        sampleCoaIds: [...accountNameByIdMap.keys()].slice(0, 5),
      }
    );
  }

  return '-';
}

const matchesAccountOption = (
  o: AccountNameOption,
  trimmed: string,
  lower: string
) =>
  String(o.value) === trimmed ||
  String(o.value).toLowerCase() === lower ||
  o.label === trimmed;

/** Find a COA option by stored id or by display label (flat or grouped lists). */
export function findAccountOptionInLists(
  needle: string | undefined,
  flat: AccountNameOption[],
  grouped: AccountNameGroupOption[]
): AccountNameOption | undefined {
  if (!needle?.trim()) return undefined;
  const trimmed = needle.trim();
  const lower = trimmed.toLowerCase();
  for (const o of flat) {
    if (matchesAccountOption(o, trimmed, lower)) return o;
  }
  for (const g of grouped) {
    for (const o of g.options ?? []) {
      if (matchesAccountOption(o, trimmed, lower)) return o;
    }
  }
  return undefined;
}

export function accountOptionExistsInLists(
  value: string,
  flat: AccountNameOption[],
  grouped: AccountNameGroupOption[]
): boolean {
  return !!findAccountOptionInLists(value, flat, grouped);
}

/**
 * Draft/API fields after picking an account from the COA dropdown.
 * Xero/QBO/Excel/Receipts all use COA row ids as option values; saving only `accountCode`
 * leaves stale `accountId` on the row and the backend ignores the change.
 */
export function applyAccountSelectionToTransactionFields(
  nextVal: string,
  accountNameByIdMap: Map<string, string>,
  accountNameOptions: AccountNameOption[],
  accountNameOptionsGrouped: AccountNameGroupOption[]
): Pick<
  AccountDisplayRowFields,
  'accountId' | 'accountCode' | 'accountName'
> {
  const trimmed = nextVal.trim();
  if (!trimmed) {
    return { accountId: null, accountCode: null, accountName: null };
  }

  const matched = findAccountOptionInLists(
    trimmed,
    accountNameOptions,
    accountNameOptionsGrouped
  );
  const inCoaById =
    accountNameByIdMap.has(trimmed) ||
    accountNameByIdMap.has(trimmed.toLowerCase()) ||
    (matched != null &&
      (accountNameByIdMap.has(matched.value) ||
        accountNameByIdMap.has(matched.value.toLowerCase())));

  if (inCoaById) {
    const accountId = accountNameByIdMap.has(trimmed)
      ? trimmed
      : matched?.value ?? trimmed;
    const accountName =
      (matched?.name ?? '').trim() ||
      lookupAccountNameInCoa(accountId, accountNameByIdMap, accountNameOptions) ||
      '';
    return {
      accountId,
      accountName: accountName || null,
      accountCode: accountId,
    };
  }

  return {
    accountCode: trimmed,
    accountName: trimmed,
    accountId: null,
  };
}

export function buildAccountSelectOptions(
  flat: AccountNameOption[],
  grouped: AccountNameGroupOption[],
  fallback?: AccountNameOption | null
): AccountNameOption[] | AccountNameGroupOption[] {
  const useGrouped = grouped.length > 0;
  if (fallback && !accountOptionExistsInLists(fallback.value, flat, grouped)) {
    if (useGrouped) {
      return [{ label: fallback.label, options: [fallback] }, ...grouped];
    }
    return [fallback, ...flat];
  }
  return useGrouped ? grouped : flat;
}

/** Value + human label + options for inline Account Name Select (never show raw id in the field). */
export function resolveAccountSelectEditState(params: {
  row: AccountDisplayRowFields | undefined;
  cellVal: unknown;
  isAccountIdProvider: boolean;
  accountNameByIdMap: Map<string, string>;
  accountNameOptions: AccountNameOption[];
  accountNameOptionsGrouped: AccountNameGroupOption[];
}): {
  value: string | undefined;
  label: string | undefined;
  options: AccountNameOption[] | AccountNameGroupOption[];
} {
  const {
    row,
    cellVal,
    isAccountIdProvider,
    accountNameByIdMap,
    accountNameOptions,
    accountNameOptionsGrouped,
  } = params;

  const displayLabelRaw = resolveAccountDisplayLabel(
    row,
    cellVal,
    accountNameByIdMap,
    accountNameOptions
  );
  const displayLabel =
    displayLabelRaw === '-' ? undefined : displayLabelRaw;

  const rawVal = resolveAccountRawValue(row, cellVal, isAccountIdProvider);

  const matched =
    (rawVal
      ? findAccountOptionInLists(
          rawVal,
          accountNameOptions,
          accountNameOptionsGrouped
        )
      : undefined) ??
    (displayLabel
      ? findAccountOptionInLists(
          displayLabel,
          accountNameOptions,
          accountNameOptionsGrouped
        )
      : undefined);

  const value = matched?.value ?? rawVal;
  const label = matched?.label ?? displayLabel ?? value;

  const fallback =
    value && label
      ? accountOptionExistsInLists(
          value,
          accountNameOptions,
          accountNameOptionsGrouped
        )
        ? null
        : { label, value }
      : null;

  const options = buildAccountSelectOptions(
    accountNameOptions,
    accountNameOptionsGrouped,
    fallback
  );

  return { value, label, options };
}

export function resolveAccountRawValue(
  row: AccountDisplayRowFields | undefined,
  cellVal: unknown,
  isAccountIdProvider: boolean
): string | undefined {
  const fromCell = cellVal != null ? String(cellVal).trim() : '';
  if (isAccountIdProvider) {
    const id =
      fromCell ||
      (row?.accountId != null ? String(row.accountId).trim() : '') ||
      (row?.accountCode != null ? String(row.accountCode).trim() : '');
    return id || undefined;
  }
  const val =
    fromCell ||
    (row?.accountCode != null ? String(row.accountCode).trim() : '') ||
    (row?.accountName != null ? String(row.accountName).trim() : '') ||
    (row?.accountId != null ? String(row.accountId).trim() : '');
  return val || undefined;
}
